import React from 'react'
import { Select, SelectProps } from 'antd'
import { useTranslation } from 'react-i18next'

export interface SelectableEntity {
  id: string
  name: string
  vatId?: string
}

export interface EntitySelectProps extends Omit<SelectProps<string>, 'options'> {
  entities: SelectableEntity[]
  loading?: boolean
  // Values to hide from the list, e.g. records already picked in sibling rows.
  excludeValues?: string[]
}

const entityLabel = (entity: SelectableEntity): string =>
  entity.vatId ? `${entity.name} · ${entity.vatId}` : entity.name

/**
 * Base single-select over a list of `{ id, name, vatId? }` records. Handles the two states where a
 * plain <Select value=...> would otherwise render the bare id: while the list is still loading, and
 * when the selected record no longer exists in the list (deleted upstream). Modelled on
 * features/scenario/components/select/ScenarioSelect.
 */
const EntitySelect: React.FC<EntitySelectProps> = ({ entities, loading, excludeValues = [], ...selectProps }) => {
  const { t } = useTranslation()
  const excluded = new Set(excludeValues)

  const options = entities
    .filter(entity => !excluded.has(entity.id))
    .map(entity => ({ value: entity.id, label: entityLabel(entity) }))

  return (
    <Select<string>
      showSearch
      optionFilterProp="label"
      loading={loading}
      options={options}
      labelRender={({ label }) =>
        loading ? `${t('global:loading', 'Ladataan')}…` : label ?? t('global:deleted', 'Poistettu')
      }
      {...selectProps}
    />
  )
}

export default EntitySelect
